import React, { useMemo } from 'react';
import EnhancedChart from './MultiChart';
import { Typography } from '@mui/material';

export interface IAppointmentTypeData {
  appointmentType: string;
  departmentUuid: string;
  departmentName: string;
  totalAppointments: number;
  avgDaysUntilAppointment: number;
  minDaysUntilAppointment: number;
  maxDaysUntilAppointment: number;
  mostCommonBookingMonth: string;
  mostCommonAppointmentMonth: string;
  mostCommonBookingDay: number;
  mostCommonAppointmentDay: number;
}

interface AppointmentTypeChartProps {
  data: IAppointmentTypeData[];
  title?: string;
  chartType?: 'bar' | 'pie' | 'donut';
  stacked?: boolean;
  horizontal?: boolean;
}

const AppointmentTypeChart = ({ 
  data,
  title = "Appointment Types by Department",
  chartType = 'bar',
  stacked = true,
  horizontal = false
}: AppointmentTypeChartProps) => {
  
  // Transform data for the chart
  const chartData = useMemo(() => {
    // Get unique departments and appointment types
    const departments = [...new Set(data.map(item => item.departmentName))].sort();
    const appointmentTypes = [...new Set(data.map(item => item.appointmentType))].sort();
    
    // Calculate total appointments
    const totalAppointments = data.reduce((sum, item) => sum + item.totalAppointments, 0);
    
    // Calculate department totals for percentage calculation
    const departmentTotals = departments.reduce((acc, dept) => {
      const deptTotal = data
        .filter(item => item.departmentName === dept)
        .reduce((sum, item) => sum + item.totalAppointments, 0);
      acc[dept] = deptTotal;
      return acc;
    }, {} as Record<string, number>);
    
    // Create series data for each appointment type (as percentages)
    const series = appointmentTypes.map(type => {
      const typeData = departments.map(dept => {
        const match = data.find(item => 
          item.departmentName === dept && 
          item.appointmentType === type
        );
        
        // Calculate percentage of this type within the department
        const count = match ? match.totalAppointments : 0;
        const deptTotal = departmentTotals[dept];
        const percentage = deptTotal > 0 ? (count / deptTotal * 100) : 0;
        
        // Round to 1 decimal place
        return Number(percentage.toFixed(1));
      });
      
      return {
        name: type,
        data: typeData
      };
    });
    
    return {
      series,
      departments,
      totalAppointments,
      departmentTotals
    };
  }, [data]);
  
  // Create percentage-based data for pie/donut charts
  const percentageData = useMemo(() => {
    if (!['pie', 'donut'].includes(chartType)) {
      return null;
    }
    
    // For pie/donut charts, we'll group by appointment type across all departments
    const appointmentTypes = [...new Set(data.map(item => item.appointmentType))].sort();
    const totalAppointments = chartData.totalAppointments;
    
    // Sum appointments by type and convert to percentages
    const typeData = appointmentTypes.map(type => {
      const sum = data
        .filter(item => item.appointmentType === type)
        .reduce((total, item) => total + item.totalAppointments, 0);
      
      // Calculate percentage of total
      const percentage = totalAppointments > 0 ? (sum / totalAppointments * 100) : 0;
      
      return {
        name: `${type} (${percentage.toFixed(1)}%)`,
        data: [percentage] // Percentage value for pie chart
      };
    });
    
    return {
      series: typeData,
      categories: ['All Departments'],
      total: 100 // Now the total is 100%
    };
  }, [data, chartType, chartData.totalAppointments]);

  // Format title component
  const titleComponent = (
    <Typography variant="h6">
      {title} {chartType !== 'pie' && chartType !== 'donut' ? '(%)' : ''}
    </Typography>
  );

  // Choose the right data based on chart type
  const chartProps = ['pie', 'donut'].includes(chartType)
    ? {
        series: percentageData?.series || [],
        title: titleComponent,
        categories: percentageData?.categories || [],
        type: chartType,
        total: percentageData?.total
      }
    : {
        series: chartData.series,
        title: titleComponent,
        categories: chartData.departments,
        type: chartType,
        stacked,
        horizontal,
        total: chartData.totalAppointments
      };

  return <EnhancedChart {...chartProps} />;
};

export default AppointmentTypeChart;